/**
 * About Us Page Component
 * 
 * Compelling story about why MoneyMojo was created, our mission, and vision.
 * Based on the PRD and README to provide authentic context about the platform.
 * 
 * Features:
 * - Compelling narrative about MoneyMojo's origin
 * - Mission and vision statements
 * - Key differentiators
 * - Dark mode support
 * - Responsive design
 * - Accessibility compliance
 * - Breadcrumb navigation for authenticated users
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Shield, Sparkles, TrendingUp, FileText, ChevronLeft } from 'lucide-react';
import { supabase } from '../lib/supabase';

export function AboutUsPage(): React.ReactElement {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setIsAuthenticated(!!session);
      } catch (error) {
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkAuth();
    
    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session);
      setIsLoading(false);
    });
    
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const handleBack = () => {
    // Navigate back to previous page or dashboard
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <div className="space-y-8">
      {/* Breadcrumb for authenticated users */}
      {!isLoading && isAuthenticated && (
        <nav aria-label="Breadcrumb" className="flex items-center space-x-2 text-sm">
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-1 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 transition-colors focus-ring rounded"
            aria-label="Go back"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            <span>Back</span>
          </button>
          <span className="text-gray-400 dark:text-gray-500" aria-hidden="true">&lt;</span>
          <span className="text-gray-900 dark:text-gray-100 font-medium">About Money Mojo</span>
        </nav>
      )}

      {/* Header */}
      <div className="text-center">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 scroll-mt-20">
          About MoneyMojo
        </h1>
        <p className="mt-3 text-lg text-gray-600 dark:text-gray-400">
          Your trusted partner in taking control of your finances
        </p>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-500">
          Built with <Heart className="inline h-4 w-4 text-red-500" /> in Australia, for Australians
        </p>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-blue-900/20 dark:via-indigo-900/20 dark:to-purple-900/20 rounded-xl shadow-sm border border-blue-200 dark:border-blue-800/50 p-8 md:p-12">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            Why We Created MoneyMojo
          </h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
            Managing your finances shouldn't be a chore. We built MoneyMojo because we were frustrated with 
            existing finance apps that lacked Australian-specific features, required hours of manual work, 
            and didn't provide the insights we needed to make better financial decisions.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 md:p-8 lg:p-10">
        <div className="prose prose-lg dark:prose-invert max-w-none">
          
          {/* The Problem */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              The Problem We're Solving
            </h2>
            <div className="space-y-4 text-gray-700 dark:text-gray-300">
              <p>
                As Australians managing our own finances, we faced several challenges that existing solutions 
                didn't address:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>
                  <strong>Lack of Australian-specific features:</strong> Most finance apps are built for 
                  international markets, missing critical features like GST handling, July-June financial 
                  year support, and ATO-compliant reporting.
                </li>
                <li>
                  <strong>Time-consuming manual work:</strong> Categorising hundreds of transactions manually 
                  each month was tedious and error-prone. We wanted automation that actually worked.
                </li>
                <li>
                  <strong>Limited insights:</strong> We needed more than just transaction lists. We wanted 
                  actionable insights that helped us understand our spending patterns and make better decisions.
                </li>
                <li>
                  <strong>Complex setup:</strong> Self-hosted solutions required technical expertise and 
                  ongoing maintenance. We wanted something simple, secure, and reliable.
                </li>
              </ul>
            </div>
          </section>

          {/* Our Solution */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Our Solution
            </h2>
            <div className="space-y-4 text-gray-700 dark:text-gray-300">
              <p>
                MoneyMojo is a modern, serverless personal finance management application built specifically 
                for the Australian market. We combine intelligent automation with comprehensive financial 
                tools to help you take control of your finances.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-1">
                    <Sparkles className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                      AI-Powered Categorisation
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Our intelligent system learns from your transactions, reducing manual categorisation 
                      time by up to 80% while maintaining 95%+ accuracy.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-1">
                    <Shield className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                      Bank-Level Security
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Built on Supabase with enterprise-grade security, Row Level Security policies, and 
                      end-to-end encryption for your peace of mind.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-1">
                    <TrendingUp className="h-6 w-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                      Actionable Insights
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Get personalised recommendations and spending pattern analysis to help you make 
                      informed financial decisions.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-1">
                    <FileText className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                      Australian Compliance
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      GST handling, financial year support, and ATO-compliant reporting built right in. 
                      No more manual calculations or format conversions.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Our Mission */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Our Mission
            </h2>
            <div className="space-y-4 text-gray-700 dark:text-gray-300">
              <p>
                To simplify financial management for Australians by providing intelligent, secure, and 
                user-friendly tools that help people take control of their finances and achieve their 
                financial goals.
              </p>
              <p>
                We believe that everyone deserves access to powerful financial management tools without 
                the complexity, cost, or privacy concerns of traditional solutions. MoneyMojo is our 
                commitment to making financial wellness accessible to all Australians.
              </p>
            </div>
          </section>

          {/* Key Features */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              What Makes MoneyMojo Different
            </h2>
            <div className="space-y-6">
              <div className="border-l-4 border-blue-500 pl-4">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  Australian-First Design
                </h3>
                <p className="text-gray-700 dark:text-gray-300">
                  Built specifically for the Australian financial landscape with support for major bank 
                  formats (CBA, ANZ, Westpac, NAB), GST handling, July-June financial year, and 
                  ATO-compliant reporting. We understand Australian financial needs because we're Australian too.
                </p>
              </div>
              <div className="border-l-4 border-purple-500 pl-4">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  Intelligent Automation
                </h3>
                <p className="text-gray-700 dark:text-gray-300">
                  Our AI-powered categorisation system learns from your transactions, reducing manual work 
                  while maintaining high accuracy. Spend less time organising and more time making informed 
                  financial decisions.
                </p>
              </div>
              <div className="border-l-4 border-green-500 pl-4">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  Comprehensive Financial Tools
                </h3>
                <p className="text-gray-700 dark:text-gray-300">
                  From transaction tracking and budgeting to goal setting and financial insights, MoneyMojo 
                  provides everything you need in one integrated platform. No need to juggle multiple apps 
                  or spreadsheets.
                </p>
              </div>
              <div className="border-l-4 border-indigo-500 pl-4">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  Privacy & Security First
                </h3>
                <p className="text-gray-700 dark:text-gray-300">
                  Your financial data is sensitive, and we treat it that way. With bank-level security, 
                  Row Level Security policies, and transparent privacy practices, you can trust that your 
                  data is protected and private.
                </p>
              </div>
              <div className="border-l-4 border-teal-500 pl-4">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  Accessibility for Everyone
                </h3>
                <p className="text-gray-700 dark:text-gray-300">
                  We're committed to making MoneyMojo accessible to everyone. Our platform meets WCAG 2.2 
                  AA standards with full keyboard navigation, screen reader support, and intuitive design 
                  that works for all users.
                </p>
              </div>
            </div>
          </section>

          {/* Our Vision */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Our Vision
            </h2>
            <div className="space-y-4 text-gray-700 dark:text-gray-300">
              <p>
                We envision a future where managing your finances is effortless, insightful, and empowering. 
                Where AI handles the tedious work, you focus on what matters, and everyone has access to 
                the tools they need to achieve financial wellness.
              </p>
              <p>
                As we grow, we're committed to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Continuously improving our AI categorisation accuracy and learning capabilities</li>
                <li>Expanding support for more Australian banks and financial institutions</li>
                <li>Adding advanced features like forecasting, goal tracking, and financial planning</li>
                <li>Maintaining our commitment to privacy, security, and accessibility</li>
                <li>Building a community of users who share our vision of simplified financial management</li>
              </ul>
            </div>
          </section>

          {/* Call to Action */}
          <section className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                Join Us on This Journey
              </h2>
              <p className="text-lg text-gray-700 dark:text-gray-300 mb-6 max-w-2xl mx-auto">
                MoneyMojo is more than just an app—it's a movement towards simpler, smarter financial 
                management. Whether you're a budget-conscious professional, a small business owner, or 
                someone looking to take control of your finances, we're here to help.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/signup"
                  className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  Get Started Free
                </a>
                <a
                  href="/help"
                  className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 dark:border-gray-600 text-base font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  Learn More
                </a>
              </div>
            </div>
          </section>

        </div>
      </div>

      {/* Footer Note */}
      <div className="text-center text-sm text-gray-500 dark:text-gray-400">
        <p>
          Built with <Heart className="inline h-4 w-4 text-red-500" /> in Australia, for Australians
        </p>
        <p className="mt-2">
          © {new Date().getFullYear()} MoneyMojo. All rights reserved.
        </p>
      </div>
    </div>
  );
}

